import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { Link, ListItemText, Tooltip } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { IEmployeeListItem } from 'src/types/employee';

type Props = {
  selected: boolean;
  onEditRow: VoidFunction;
  row: IEmployeeListItem;
  onDeleteRow: VoidFunction;
};

export default function EmployeeTableRow({ row, selected, onEditRow, onDeleteRow }: Props) {
  const { _id, name, employeeNumber, baseSalary, epf, updatedAt } = row;

  const { user: authUser } = useAuthContext();

  const confirm = useBoolean();

  const popover = usePopover();

  return (
    <>
      <Link
        component={RouterLink}
        href={paths.dashboard.employees.view(_id)}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'contents',
        }}
      >
        <TableRow hover selected={selected}>
          <TableCell align="left">
            <ListItemText
              primary={name}
              secondary={employeeNumber}
              primaryTypographyProps={{ typography: 'body2' }}
              secondaryTypographyProps={{
                component: 'span',
                color: 'text.disabled',
              }}
            />
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fCurrencyRupees(baseSalary * 100)}</TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap' }}>{epf ? 'Yes' : 'No'}</TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {fDateTime(updatedAt)}
          </TableCell>

          <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
            <Link component={RouterLink} href={paths.dashboard.employees.view(_id)} mr={2}>
              <Button variant="soft" color="primary">
                View More
              </Button>
            </Link>

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.UPDATE_EMPLOYEE,
              AuthPermissions.DELETE_EMPLOYEE,
              AuthPermissions.CREATE_ADVANCED_PAYMENT,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Tooltip title="More options">
                <IconButton
                  color={popover.open ? 'inherit' : 'default'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    popover.onOpen(e);
                  }}
                >
                  <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      </Link>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 180 }}
      >
        {hasPermission(
          authUser,
          'any',
          AuthPermissions.UPDATE_EMPLOYEE,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <MenuItem
            onClick={() => {
              onEditRow();
              popover.onClose();
            }}
          >
            <Iconify icon="solar:pen-bold" />
            Edit
          </MenuItem>
        )}

        {hasPermission(
          authUser,
          'any',
          AuthPermissions.CREATE_ADVANCED_PAYMENT,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <Link component={RouterLink} href={paths.dashboard.advancedPayments.new(_id)}>
            <MenuItem sx={{ color: 'success.main' }}>
              <Iconify icon="solar:banknote-2-bold" />
              Make Payment
            </MenuItem>
          </Link>
        )}

        {hasPermission(
          authUser,
          'any',
          AuthPermissions.DELETE_EMPLOYEE,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) &&
          authUser?._id !== _id && (
            <MenuItem
              onClick={() => {
                confirm.onTrue();
                popover.onClose();
              }}
              sx={{ color: 'error.main' }}
            >
              <Iconify icon="solar:trash-bin-trash-bold" />
              Delete
            </MenuItem>
          )}
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete Employee"
        content={`Are you sure want to delete ${name}? This action will permenantly delete ${name}'s details from the system`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              confirm.onFalse();
              onDeleteRow();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}
